import { BroadcastChannel } from 'broadcast-channel';
import { useEffect, useRef } from 'react';

/**
 * useBroadcaster is a convenience hook around cross-tab event management. It allows
 * one tab to broadcast an event to other tabs and send data. The hook returns a dispatch
 * function that can be called to initiate an event.
 *
 * @param id the unique channel to send events on
 * @param listener how to handle incoming events on the channel
 * @returns an object with a dispatch function to dispatch an event.
 */
function useBroadcaster<T>(id: string, listener: (ev?: T) => void) {
  const channel = useRef(new BroadcastChannel(id));

  useEffect(() => {
    channel.current.onmessage = (ev) => listener(ev);

    return () => {
      channel.current.close();
    };
  }, []);

  return {
    // we MUST wrap this function because the broadcastChannel reference may change
    dispatch: (ev?: T) => channel.current.postMessage(ev),
  };
}

export default useBroadcaster;
